import React from 'react';
import shallow from 'zustand/shallow';
import styled from 'styled-components';
import useStore from '~/store';
import useColumnSum from '~/hooks/useColumnSum';
import useDatum from '~/hooks/useDatum';
import Checkbox from '~/components/Checkbox';

const Flex = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Container = styled.div`
  width: 100%;

  @media screen and (min-width: 768px) {
    width: 50%;
  }
`;

const ColumnGroupHeader = styled.div`
  padding: 5px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid rgb(181, 198, 213);
`;

const ColumnGroupFooter = styled.div`
  margin-top: 15px;
  padding-top: 5px;
  margin-right: 15px;
  border-top: 1px solid rgb(181, 198, 213);
`;

const ColumnGroupExplainer = styled.div`
  margin: 15px 0 8px 0;
  padding-right: 15px;
`;

const toggleColumnSelector = (s) => s.toggleColumn;

function Column({ name, value, isActive, side, color }) {
  const toggleColumn = useStore(toggleColumnSelector);

  const handleCheckbox = () => {
    toggleColumn(name, side);
  };

  return <Checkbox label={name} value={value} checked={isActive} onChange={handleCheckbox} bgColor={color} />;
}

const selector = (s) => ({
  leftColumns: s.leftColumns,
  rightColumns: s.rightColumns,
  leftColumnsLabel: s.leftColumnsLabel,
  rightColumnsLabel: s.rightColumnsLabel,
  age: s.age,
  currentView: s.currentView,
  leftSheet: s.leftSheet,
  rightSheet: s.rightSheet,
});

const ColumnGroup = ({
  columns,
  data,
  sum,
  label,
  side,
  age,
  unitLabel,
  unitDescriptor,
  groupLabel,
  capitalJ = true,
}) => {
  const toggleColumns = useStore((s) => s.toggleColumns);
  const columnsActive = columns.some((col) => col.isActive);
  const footerLabel = side === 'left' ? 'u.a. ohne Unternehmensteuern' : 'u.a. ohne Infrastruktur';
  const j = capitalJ ? 'J' : 'j';

  return (
    <Container>
      <ColumnGroupExplainer>
        {unitDescriptor} {label} von {age}-{j}ährigen {groupLabel} in {unitLabel} pro Jahr
      </ColumnGroupExplainer>
      <ColumnGroupHeader>
        <Checkbox
          style={{ fontWeight: 'bold' }}
          label={label}
          checked={columnsActive}
          onChange={() => toggleColumns(side)}
          value={sum}
        />
      </ColumnGroupHeader>
      {columns.map((col) => (
        <Column name={col.name} value={data?.[col.name]} isActive={col.isActive} side={side} color={col.color} />
      ))}
      <ColumnGroupFooter>{footerLabel}</ColumnGroupFooter>
    </Container>
  );
};

export default function ColumnSelect() {
  const { leftColumns, rightColumns, leftColumnsLabel, rightColumnsLabel, currentView, age, leftSheet, rightSheet } =
    useStore(selector, shallow);
  const { leftSum, rightSum } = useColumnSum();
  const { leftDatum, rightDatum } = useDatum();

  return (
    <>
      <Flex>
        <ColumnGroup
          unitLabel={currentView.unitLabel}
          unitDescriptor={currentView.unitDescriptor}
          age={age}
          data={leftDatum}
          columns={leftColumns}
          sum={leftSum}
          label={leftColumnsLabel}
          side="left"
          groupLabel={leftSheet.groupLabel}
          capitalJ={leftSheet.capitalJ}
        />
        <ColumnGroup
          unitLabel={currentView.unitLabel}
          unitDescriptor={currentView.unitDescriptor}
          age={age}
          data={rightDatum}
          columns={rightColumns}
          sum={rightSum}
          label={rightColumnsLabel}
          side="right"
          groupLabel={rightSheet.groupLabel}
          capitalJ={rightSheet.capitalJ}
        />
      </Flex>
    </>
  );
}
